<script lang="ts">
  import { selectedTeamStore, type TeamApp } from "$lib/stores/teams";
  import * as Drawer from "$ui/drawer";
  import { Button } from "$ui/button";
  import { LoadingSpinner } from "$ui/loading-spinner";
  import { SearchBar } from "$ui/search-bar";
  import { fuzzyMatch } from "$utils/helpers";

  import AppForm from "$lib/components/apps/AppForm.svelte";
  import AppPanel from "$lib/components/apps/AppPanel.svelte";
  import AppIntegrationInstructions from "$lib/components/apps/AppIntegrationInstructions.svelte";
  import SectionTitle from "$lib/components/shared/SectionTitle.svelte";

  let filteredApps: TeamApp[] = [];

  let createOpen = false;
  let isLoading: boolean = false;

  let step = 0;
  $: team = $selectedTeamStore;
  $: {
    filteredApps = team?.apps ?? [];
    filteredApps.sort((a, b) => a.id.localeCompare(b.id));
  }
  $: canEdit = team?.userClaims.includes("apps:write");
  $: step;

  const onSearch = (newTerm: string) => {
    filteredApps =
      (newTerm !== ""
        ? team?.apps.filter((app) => {
            return fuzzyMatch(app.id, newTerm) || fuzzyMatch(app.description, newTerm);
          })
        : team?.apps) ?? [];
  };
</script>

<SectionTitle>Apps</SectionTitle>
<div class="flex flex-col-reverse xs:flex-row justify-between xs:items-center gap-4">
  <div class="w-full md:max-w-[300px]">
    <SearchBar onChange={onSearch} />
  </div>
  <AppIntegrationInstructions />
  <Drawer.Root bind:open={createOpen} preventScroll={false} closeFocus={null} onOpenChange={() => {}}>
    <Drawer.Trigger disabled={!canEdit}>
      <Button on:click={() => (createOpen = true)} class="w-full min-h-[46px]" disabled={!canEdit}>+ Add App</Button>
    </Drawer.Trigger>
    <Drawer.Content>
      <Drawer.Header class="pt-8 px-6 pb-4 relative">
        <Drawer.Title class="text-xl text-left">Create New App</Drawer.Title>
        <Drawer.Description class="p text-base text-left">Create a new app to start connecting data</Drawer.Description>
      </Drawer.Header>
      <div class="px-6 flex-1 flex flex-col overflow-auto">
        <AppForm app={undefined} onComplete={() => (createOpen = false)} />
      </div>
    </Drawer.Content>
  </Drawer.Root>
</div>

{#if isLoading}
  <div class="flex justify-center items-center">
    <LoadingSpinner />
  </div>
{:else if team?.apps.length ?? 0 > 0}
  {#if filteredApps.length === 0}
    <div class="flex flex-col gap-6 justify-center items-center p-8 card">
      <div class="flex flex-col gap-2 text-center">
        <div class="text-2xl font-semibold">No apps found</div>
        <p>No apps found. Try a different search term</p>
      </div>
    </div>
  {/if}
  <div class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-8">
    {#each filteredApps as app (app.id)}
      <AppPanel {app} {canEdit} />
    {/each}
  </div>
{:else}
  <div class="flex flex-col gap-6 justify-center items-center p-8 card">
    <div class="flex flex-col gap-2 text-center">
      <div class="text-2xl font-semibold">You don’t have any apps yet!</div>
      <p>No apps found. Click the "+ Add" button to create a new app.</p>
    </div>
    <Button on:click={() => (createOpen = true)} disabled={!canEdit}>Add First App</Button>
  </div>
{/if}
